import { useEffect, useRef, useState } from 'react'

/**
 * Paces a streamed string so it flows at a steady, readable rate instead of
 * jumping a network-chunk at a time. The reveal rate scales with the
 * unrevealed backlog — a floor keeps short replies moving, a ceiling stops a
 * large burst from becoming an instant dump — and keeps draining after the
 * stream flag drops, so the final atomic message never snaps in.
 *
 * Rows never observed streaming (rehydrated history) render whole with no
 * loop, as does everything under prefers-reduced-motion.
 */

const MIN_CHARS_PER_SEC = 90
const MAX_CHARS_PER_SEC = 2600
// Reveal ~this fraction of the backlog per second (exponential catch-up).
const BACKLOG_DRAIN_PER_SEC = 3
// Background tabs starve rAF; a huge dt must not dump the whole backlog.
const MAX_FRAME_DT_S = 0.1

const prefersReducedMotion = () =>
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches

export const useSmoothStreamText = (
  target: string,
  streaming: boolean,
): { text: string; animating: boolean } => {
  const everStreamed = useRef(streaming)
  if (streaming) everStreamed.current = true

  const paced = everStreamed.current && !prefersReducedMotion()
  // The ref drives the rAF loop (setState updaters run at render time, not
  // synchronously — the loop can't read its own progress through state).
  const revealedRef = useRef(paced ? 0 : target.length)
  const [revealedLength, setRevealedLength] = useState(revealedRef.current)

  // Final atomic message may normalize to something shorter — snap, never hang.
  const length = Math.min(revealedLength, target.length)
  const backlog = paced ? target.length - length : 0
  const animating = paced && (streaming || backlog > 0)

  useEffect(() => {
    if (!paced) return
    revealedRef.current = Math.min(revealedRef.current, target.length)
    if (revealedRef.current >= target.length) return
    let raf = 0
    let lastFrame = performance.now()
    const step = (now: number) => {
      const dt = Math.min((now - lastFrame) / 1000, MAX_FRAME_DT_S)
      lastFrame = now
      const len = revealedRef.current
      const remaining = target.length - len
      if (remaining <= 0) return // caught up; the next chunk re-arms via deps
      const rate = Math.min(
        Math.max(remaining * BACKLOG_DRAIN_PER_SEC, MIN_CHARS_PER_SEC),
        MAX_CHARS_PER_SEC,
      )
      let next = Math.min(len + Math.max(1, Math.round(rate * dt)), target.length)
      // Never cut a surrogate pair in half (emoji renders as tofu mid-pair).
      const edge = target.charCodeAt(next - 1)
      if (edge >= 0xd800 && edge <= 0xdbff && next < target.length) next += 1
      revealedRef.current = next
      setRevealedLength(next)
      if (next < target.length) raf = requestAnimationFrame(step)
    }
    raf = requestAnimationFrame(step)
    return () => cancelAnimationFrame(raf)
  }, [paced, target])

  return {
    text: paced ? target.slice(0, length) : target,
    animating,
  }
}
